import { Grid, useEventCallback } from '@mui/material';
import { CivilUnionVM } from 'mutation/view-models/civil-union';
import { memo, useCallback } from 'react';
import { SubjectData } from 'shell/models/person';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import PmiTextField from 'shell/components/shared/text-field';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { AccordionGeneralityForm } from 'shell/view-models/accordion-generality-form';
import DataMerge from '../data-merge';
import { transformerPersonToCivilUnion } from 'mutation/utils/transformer-person-to-civil-union';
import { FormikErrors, FormikTouched } from 'formik';
import PmiDivider from 'shell/components/shared/divider';
import CohabitantGenerality from '../cohabitant-generality';
import DocumentActForm from 'shell/components/shared/act';
import { generatePath, useParams } from 'react-router-dom';
import ResearchSipoPerson from 'shell/components/shared/research-sipo-accordion';
import { PersonalDataSearchVM } from 'shell/view-models/personal-data';

interface CivilUnionFormProps {
  values?: CivilUnionVM | null;
  onChange: (key: keyof CivilUnionVM, value: CivilUnionVM[keyof CivilUnionVM]) => void;
  onChangeValues: (value: CivilUnionVM) => void;
  errors: FormikErrors<CivilUnionVM>;
  touched: FormikTouched<CivilUnionVM>;
  disabled?: boolean;
  checkPerson: (person: SubjectData) => boolean;
  errorMessage?: string;
  editSipoUrl?: string;
}

const CivilUnionFormComponent = ({
  values,
  onChange,
  onChangeValues,
  errors,
  touched,
  disabled = false,
  editSipoUrl = '/mutazioni/persona/unione-civile/non-residenti/registrazione/:idIntestatario/:idSchedaSoggettoANPR/persona-sipo',
}: CivilUnionFormProps): React.ReactElement => {
  // const [openError, setOpenError] = useState<boolean>(false);

  const { idIntestatario, idSchedaSoggettoANPR } = useParams();

  // const onCloseErrorModal = useEventCallback((value: boolean) => {
  //   setOpenError(value);
  // });

  // on search person
  const onSearch = useEventCallback((fields: PersonalDataSearchVM) => {
    const responseCasted = fields;

    const person = responseCasted.subjectData || {};

    const personItem = person;

    const personValue: CivilUnionVM = transformerPersonToCivilUnion(personItem);

    // const canBeMutated = checkPerson(personItem);
    // if (!canBeMutated) {
    //   setOpenError(true);
    // }

    const newCivilUnion: CivilUnionVM = {
      ...values,
      searchPerson: undefined, // reset
      ...personValue,
    };

    onChangeValues(newCivilUnion);
  });

  const onFieldPersonChange = useEventCallback(
    (key: keyof AccordionPersonForm, value: AccordionPersonForm[keyof AccordionPersonForm]) => {
      onChange('person', {
        ...(values?.person || {}),
        [key]: value,
      });
    }
  );

  const onFieldGeneralityChange = useEventCallback(
    (
      key: keyof AccordionGeneralityForm,
      value: AccordionGeneralityForm[keyof AccordionGeneralityForm]
    ) => {
      onChange('generality', {
        ...(values?.generality || {}),
        [key]: value,
      });
    }
  );

  const onFieldsGeneralityChange = useEventCallback((vals: AccordionGeneralityForm) => {
    onChange('generality', {
      ...(values?.generality || {}),
      ...vals,
    });
  });

  const onFieldsPersonChange = useCallback(
    (vals) => {
      onChange('person', {
        ...(values?.person || {}),
        ...vals,
      });
    },
    [values?.person, onChange]
  );

  return (
    <Grid container spacing={1}>
      {/* <ErrorModal
        open={openError}
        onClose={onCloseErrorModal}
        message={`Stato civile del soggetto incongruente con ${errorMessage}`}
      /> */}
      <Grid item xs={12}>
        <ResearchSipoPerson
          onSearchEnd={onSearch}
          url={`${__PERSON_API_URL__}/non-residenti/ricerca-intestatario-matrimonio`}
          redirectPath={generatePath(editSipoUrl, {
            idIntestatario,
            idSchedaSoggettoANPR,
          })}
          initialValues={values?.person ? { filter: values.person } : undefined}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <PmiTextField
          label="Ordine unione"
          aria-label="Ordine unione"
          id="union-order"
          fullWidth
          value={values?.unionOrder || ''}
          disabled={disabled || values?.disabled}
          onChange={(event) => {
            onChange('unionOrder', event.target.value);
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTitleDivider>Unito civilmente</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <CohabitantGenerality
          person={values?.person}
          generality={values?.generality}
          onChangePerson={onFieldPersonChange}
          onChangeGenerality={onFieldGeneralityChange}
          onChangeFieldsGenerality={onFieldsGeneralityChange}
          onChangeFieldsPerson={onFieldsPersonChange}
          disabled={disabled || values?.disabled}
          errorsGenerality={errors?.generality as FormikErrors<AccordionGeneralityForm>}
          touchedGenerality={
            touched?.generality as unknown as FormikTouched<AccordionGeneralityForm>
          }
          errorsPerson={errors?.person as FormikErrors<AccordionPersonForm>}
          touchedPerson={touched?.person as unknown as FormikTouched<AccordionPersonForm>}
        />
      </Grid>

      <DataMerge
        onChange={onChange}
        values={values}
        onChangeValues={onChangeValues}
        errors={errors as FormikErrors<CivilUnionVM>}
        touched={touched as FormikTouched<CivilUnionVM>}
        disabled={disabled}
      />
      <Grid item xs={12}>
        <PmiTitleDivider>Atto di unione</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <PmiDivider />
      </Grid>
      <Grid item xs={12}>
        <DocumentActForm
          values={values || undefined}
          onChange={(key, value) => {
            onChange(key, value);
          }}
          onChanges={(fields) => {
            onChangeValues(fields);
          }}
          errors={errors}
          requiredDigital={{
            documentDate: false,
          }}
          touched={touched}
          disabled={disabled}
          isNotDisabledData
        />
      </Grid>
    </Grid>
  );
};

CivilUnionFormComponent.displayName = 'Accordion Civil Union form component';

const CivilUnionForm = memo(CivilUnionFormComponent);

export default CivilUnionForm;
